import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Select,
  DatePicker,
  InputNumber,
  Input,
  Modal,
  message,
  Tag,
  Drawer,
  Descriptions,
  Popconfirm,
  Switch,
} from 'antd';
import { PlusOutlined, EyeOutlined, PrinterOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import api from '@/services/api.service';
import intercompanyService, { type IcSettlement } from '@/services/intercompany.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import { printIcSettlement } from '@/utils/intercompanyPrint';

const { Title, Paragraph } = Typography;

type CoaRow = { id: string; account_code: string; account_name: string; account_type?: string };

const IntercompanySettlementsPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const qc = useQueryClient();
  const { formatCurrency } = useCurrency();
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<IcSettlement | null>(null);
  const [counterpartyId, setCounterpartyId] = useState<string>();
  const [settlementType, setSettlementType] = useState<'Netting' | 'Cash'>('Netting');
  const [settlementDate, setSettlementDate] = useState(dayjs());
  const [nettedAmount, setNettedAmount] = useState<number | null>(null);
  const [payerBankGl, setPayerBankGl] = useState<string>();
  const [payeeBankGl, setPayeeBankGl] = useState<string>();
  const [notes, setNotes] = useState('');
  const [postImmediately, setPostImmediately] = useState(true);

  const { data: affiliates = [] } = useQuery({
    queryKey: ['intercompany-affiliates'],
    queryFn: () => intercompanyService.listAffiliates(),
  });

  const { data: balances = [] } = useQuery({
    queryKey: ['intercompany-balance'],
    queryFn: () => intercompanyService.balanceReport(),
  });

  const { data: list = [], isLoading } = useQuery({
    queryKey: ['intercompany-settlements'],
    queryFn: () => intercompanyService.listSettlements(),
  });

  const { data: bankAccounts = [] } = useQuery({
    queryKey: ['ic-bank-gl-accounts'],
    queryFn: async () => {
      const res = await api.get('/finance/chart-of-accounts');
      const rows = (res.data?.data ?? res.data ?? []) as CoaRow[];
      return rows.filter(
        (a) =>
          a.account_code?.startsWith('10') ||
          /cash|bank/i.test(a.account_name || '') ||
          a.account_code === '1000' ||
          a.account_code === '1010'
      );
    },
    enabled: open && settlementType === 'Cash',
  });

  const pair = balances.find((b) => b.affiliate_company_id === counterpartyId);
  const maxNettable = pair ? Math.min(pair.due_from_balance, pair.due_to_balance) : 0;
  const netCash = pair ? Math.abs(pair.due_from_balance - pair.due_to_balance) : 0;

  const createMut = useMutation({
    mutationFn: () =>
      intercompanyService.createSettlement({
        counterparty_company_id: counterpartyId,
        settlement_date: settlementDate.format('YYYY-MM-DD'),
        settlement_type: settlementType,
        netted_amount: nettedAmount ?? undefined,
        payer_bank_gl_account_id: settlementType === 'Cash' ? payerBankGl : undefined,
        payee_bank_gl_account_id: settlementType === 'Cash' ? payeeBankGl : undefined,
        notes: notes || undefined,
      }),
    onSuccess: async (row) => {
      message.success(postImmediately ? t('settlementCreated') : t('settlementDraftCreated'));
      setOpen(false);
      qc.invalidateQueries({ queryKey: ['intercompany-settlements'] });
      if (postImmediately) {
        await intercompanyService.postSettlement(row.id);
        message.success(t('settlementPosted'));
        qc.invalidateQueries({ queryKey: ['intercompany-settlements'] });
        qc.invalidateQueries({ queryKey: ['intercompany-balance'] });
      }
    },
    onError: (e: Error) => message.error(e.message || t('failedGeneric')),
  });

  const postMut = useMutation({
    mutationFn: (id: string) => intercompanyService.postSettlement(id),
    onSuccess: () => {
      message.success(t('settlementPosted'));
      qc.invalidateQueries({ queryKey: ['intercompany-settlements'] });
      qc.invalidateQueries({ queryKey: ['intercompany-balance'] });
      setDetail(null);
    },
    onError: (e: Error) => message.error(e.message || t('failedGeneric')),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => intercompanyService.deleteSettlement(id),
    onSuccess: () => {
      message.success(t('deleted'));
      qc.invalidateQueries({ queryKey: ['intercompany-settlements'] });
      setDetail(null);
    },
    onError: (e: Error) => message.error(e.message || t('failedGeneric')),
  });

  const nameOf = (id: string) => {
    const a = affiliates.find((x) => x.company_id === id);
    return a ? `${a.company_code} — ${a.company_name}` : t('affiliate');
  };

  const openDetail = async (id: string) => {
    try {
      const row = await intercompanyService.getSettlement(id);
      setDetail(row);
    } catch (e) {
      message.error(e instanceof Error ? e.message : t('failedToLoad'));
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            {t('intercompanySettlements')}
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {t('intercompanySettlementsSubtitle')}
          </Paragraph>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
          {t('newSettlement')}
        </Button>
      </Space>

      <Table
        loading={isLoading}
        rowKey="id"
        dataSource={list}
        columns={[
          {
            title: t('settlementNo'),
            dataIndex: 'settlement_number',
            render: (v, r) => <a onClick={() => openDetail(r.id)}>{v}</a>,
          },
          {
            title: t('date'),
            dataIndex: 'settlement_date',
            render: (d) => (d ? dayjs(d).format('DD MMM YYYY') : '—'),
          },
          { title: t('type'), dataIndex: 'settlement_type' },
          {
            title: t('counterparty'),
            dataIndex: 'counterparty_company_id',
            render: (id) => nameOf(id),
          },
          {
            title: t('amount'),
            dataIndex: 'netted_amount',
            align: 'right',
            render: (v, r) => formatCurrency(Number(v), r.currency),
          },
          {
            title: tc('status'),
            dataIndex: 'status',
            render: (s) => <Tag color={s === 'Posted' ? 'blue' : 'default'}>{s}</Tag>,
          },
          {
            title: tc('actions'),
            key: 'actions',
            width: 280,
            render: (_, r) => (
              <Space size={0} wrap>
                <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => openDetail(r.id)}>
                  {tc('view')}
                </Button>
                <Button
                  type="link"
                  size="small"
                  icon={<PrinterOutlined />}
                  onClick={() =>
                    printIcSettlement(r, {
                      formatCurrency,
                      counterpartyName: nameOf(r.counterparty_company_id),
                    })
                  }
                >
                  {t('print')}
                </Button>
                {r.status === 'Draft' && (
                  <>
                    <Button type="link" size="small" loading={postMut.isPending} onClick={() => postMut.mutate(r.id)}>
                      {tc('post')}
                    </Button>
                    <Popconfirm title={t('deleteDraftConfirm')} onConfirm={() => deleteMut.mutate(r.id)}>
                      <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                        {tc('delete')}
                      </Button>
                    </Popconfirm>
                  </>
                )}
              </Space>
            ),
          },
        ]}
      />

      <Modal
        title={t('icSettlement')}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => {
          if (!counterpartyId) {
            message.warning(t('selectCounterparty'));
            return;
          }
          if (settlementType === 'Cash' && (!payerBankGl || !payeeBankGl)) {
            message.warning(t('cashSettlementRequiresBanks'));
            return;
          }
          createMut.mutate();
        }}
        confirmLoading={createMut.isPending}
        okText={postImmediately ? t('createAndPost') : t('createDraft')}
        width={560}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <Typography.Text strong>{t('settlementType')}</Typography.Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              value={settlementType}
              onChange={setSettlementType}
              options={[
                { value: 'Netting', label: t('nettingType') },
                { value: 'Cash', label: t('cashType') },
              ]}
            />
          </div>
          <div>
            <Typography.Text strong>{t('affiliate')}</Typography.Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              value={counterpartyId}
              onChange={(v) => {
                setCounterpartyId(v);
                setNettedAmount(null);
              }}
              options={affiliates.map((a) => ({
                value: a.company_id,
                label: `${a.company_code} — ${a.company_name}`,
              }))}
            />
          </div>
          {pair && settlementType === 'Netting' && (
            <Paragraph type="secondary" style={{ margin: 0 }}>
              {t('openDueFromDueToMax', {
                from: formatCurrency(pair.due_from_balance),
                to: formatCurrency(pair.due_to_balance),
                max: formatCurrency(maxNettable),
              })}
            </Paragraph>
          )}
          {pair && settlementType === 'Cash' && (
            <Paragraph type="secondary" style={{ margin: 0 }}>
              {t('netOpenDueAbs', { amount: formatCurrency(netCash) })}
            </Paragraph>
          )}
          <div>
            <Typography.Text strong>{t('settlementDate')}</Typography.Text>
            <DatePicker
              style={{ width: '100%', marginTop: 8 }}
              value={settlementDate}
              onChange={(d) => d && setSettlementDate(d)}
            />
          </div>
          <div>
            <Typography.Text strong>
              {settlementType === 'Cash' ? t('cashAmountOptional') : t('nettedAmountOptional')}
            </Typography.Text>
            <InputNumber
              style={{ width: '100%', marginTop: 8 }}
              min={0}
              max={(settlementType === 'Cash' ? netCash : maxNettable) || undefined}
              value={nettedAmount ?? undefined}
              onChange={(v) => setNettedAmount(v == null ? null : Number(v))}
            />
          </div>
          {settlementType === 'Cash' && (
            <>
              <div>
                <Typography.Text strong>{t('payerBankCashGl')}</Typography.Text>
                <Select
                  style={{ width: '100%', marginTop: 8 }}
                  showSearch
                  optionFilterProp="label"
                  value={payerBankGl}
                  onChange={setPayerBankGl}
                  options={bankAccounts.map((a) => ({
                    value: a.id,
                    label: `${a.account_code} — ${a.account_name}`,
                  }))}
                  placeholder={t('selectPayerBankGl')}
                />
                <Input
                  style={{ marginTop: 8 }}
                  value={payerBankGl}
                  onChange={(e) => setPayerBankGl(e.target.value)}
                  placeholder={t('orPasteAffiliatePayerBankGl')}
                />
              </div>
              <div>
                <Typography.Text strong>{t('payeeBankCashGl')}</Typography.Text>
                <Select
                  style={{ width: '100%', marginTop: 8 }}
                  showSearch
                  optionFilterProp="label"
                  value={payeeBankGl}
                  onChange={setPayeeBankGl}
                  options={bankAccounts.map((a) => ({
                    value: a.id,
                    label: `${a.account_code} — ${a.account_name}`,
                  }))}
                  placeholder={t('selectPayeeBankGl')}
                />
                <Input
                  style={{ marginTop: 8 }}
                  value={payeeBankGl}
                  onChange={(e) => setPayeeBankGl(e.target.value)}
                  placeholder={t('orPastePayeeBankGl')}
                />
              </div>
            </>
          )}
          <div>
            <Typography.Text strong>{t('notes')}</Typography.Text>
            <Input.TextArea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} style={{ marginTop: 8 }} />
          </div>
          <Space>
            <Switch checked={postImmediately} onChange={setPostImmediately} />
            <Typography.Text>{t('postImmediatelyAfterCreate')}</Typography.Text>
          </Space>
        </Space>
      </Modal>

      <Drawer
        title={detail?.settlement_number || t('settlement')}
        open={!!detail}
        onClose={() => setDetail(null)}
        width={480}
        extra={
          detail ? (
            <Space>
              <Button
                icon={<PrinterOutlined />}
                onClick={() =>
                  printIcSettlement(detail, {
                    formatCurrency,
                    counterpartyName: nameOf(detail.counterparty_company_id),
                  })
                }
              >
                {t('print')}
              </Button>
              {detail.status === 'Draft' && (
                <Button type="primary" loading={postMut.isPending} onClick={() => postMut.mutate(detail.id)}>
                  {tc('post')}
                </Button>
              )}
            </Space>
          ) : null
        }
      >
        {detail && (
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label={t('type')}>{detail.settlement_type}</Descriptions.Item>
            <Descriptions.Item label={tc('status')}>
              <Tag color={detail.status === 'Posted' ? 'blue' : 'default'}>{detail.status}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('date')}>
              {dayjs(detail.settlement_date).format('DD MMM YYYY')}
            </Descriptions.Item>
            <Descriptions.Item label={t('counterparty')}>
              {nameOf(detail.counterparty_company_id)}
            </Descriptions.Item>
            <Descriptions.Item label={t('dueFromBefore')}>
              {formatCurrency(Number(detail.due_from_before), detail.currency)}
            </Descriptions.Item>
            <Descriptions.Item label={t('dueToBefore')}>
              {formatCurrency(Number(detail.due_to_before), detail.currency)}
            </Descriptions.Item>
            <Descriptions.Item label={t('settledAmount')}>
              {formatCurrency(Number(detail.netted_amount), detail.currency)}
            </Descriptions.Item>
            {detail.notes && <Descriptions.Item label={t('notes')}>{detail.notes}</Descriptions.Item>}
          </Descriptions>
        )}
      </Drawer>
    </div>
  );
};

export default IntercompanySettlementsPage;
